import type { CSSProperties, ReactNode } from 'react';
import {
  Battery,
  ClipboardCheck,
  FileText,
  FolderKanban,
  Image as ImageIcon,
  Info,
  MapPin,
  Paperclip,
  ShieldCheck,
  Sparkles,
  Sun,
  TriangleAlert,
  Wind,
} from 'lucide-react';
import { SOVEREIGN } from '../../utils/epcProjectVisual';
import type { DocTag } from './types';

/** Engraved bottle-green icon-tile gradient. */
export const TILE = `linear-gradient(135deg, ${SOVEREIGN.bottle}, ${SOVEREIGN.title})`;

/** Flat Sovereign primary button (no gradient). */
export const PRIMARY_BTN: CSSProperties = {
  background: SOVEREIGN.btn,
  border: `1px solid ${SOVEREIGN.btnBorder}`,
  boxShadow: SOVEREIGN.btnShadow,
};

/** Sovereign input classes — ivory fill, racing-green focus. */
export const svInput =
  'w-full rounded-xl border px-3 py-2.5 text-sm font-medium outline-none transition ' +
  'bg-[#fcfaf3] border-[rgba(11,59,46,0.28)] text-[#0B3B2E] placeholder:text-[#7C8A82] ' +
  'hover:border-[rgba(51,146,116,0.6)] focus:border-[#0B3B2E] focus:ring-[3px] focus:ring-[rgba(166,206,57,0.22)]';

export const svSecondaryBtn: CSSProperties = {
  background: SOVEREIGN.inputBg,
  border: `1px solid ${SOVEREIGN.inputBorder}`,
  color: SOVEREIGN.title,
};

export function FieldLabel({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div className="mb-1.5 flex items-center justify-between gap-2">
      <label className="text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: SOVEREIGN.ink }}>
        {children}
      </label>
      {hint ? <span className="text-[10px]" style={{ color: SOVEREIGN.kpiCaption }}>{hint}</span> : null}
    </div>
  );
}

export function HelperText({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'warn' | 'info' }) {
  const color = tone === 'warn' ? '#8A5A08' : tone === 'info' ? SOVEREIGN.brand : SOVEREIGN.kpiCaption;
  const Icon = tone === 'warn' ? TriangleAlert : tone === 'info' ? Sparkles : Info;
  return (
    <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium" style={{ color }}>
      <Icon className="h-3 w-3" strokeWidth={2.4} />
      {children}
    </p>
  );
}

export function SectionHeading({ icon: Icon, title, subtitle }: { icon: typeof Sun; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg text-white shadow-sm" style={{ background: TILE }}>
        <Icon className="h-4 w-4" strokeWidth={2} />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-extrabold tracking-tight" style={{ color: SOVEREIGN.title }}>{title}</p>
        {subtitle ? <p className="text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>{subtitle}</p> : null}
      </div>
    </div>
  );
}

/** Dashed banknote inner rule — absolutely positioned inside a relative card. */
export function BanknoteRule() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute"
      style={{ inset: 7, border: '1px dashed rgba(11,59,46,0.18)', borderRadius: 11 }}
    />
  );
}

/* ---------- Project-type theming ---------- */

export type ThemeKey = 'solar' | 'wind' | 'hybrid' | 'bess' | 'generic';

export function themeFromCategory(category: string | undefined): ThemeKey {
  if (category === 'solar' || category === 'wind' || category === 'hybrid' || category === 'bess') return category;
  return 'generic';
}

export const THEME_STYLES: Record<ThemeKey, { gradient: string; accent: string; accentSoft: string; label: string; icon: typeof Sun }> = {
  solar:   { gradient: 'linear-gradient(180deg,#F8F2E2 0%,#F2EADB 100%)', accent: '#9A7D3C', accentSoft: 'rgba(154,125,60,0.14)', label: 'Solar',  icon: Sun },
  wind:    { gradient: 'linear-gradient(180deg,#F8F2E2 0%,#F2EADB 100%)', accent: '#3F6B82', accentSoft: 'rgba(63,107,130,0.14)', label: 'Wind',   icon: Wind },
  hybrid:  { gradient: 'linear-gradient(180deg,#F8F2E2 0%,#F2EADB 100%)', accent: '#1F5A47', accentSoft: 'rgba(31,90,71,0.14)',  label: 'Hybrid', icon: Sparkles },
  bess:    { gradient: 'linear-gradient(180deg,#F8F2E2 0%,#F2EADB 100%)', accent: '#6B5A8A', accentSoft: 'rgba(107,90,138,0.14)', label: 'BESS',  icon: Battery },
  generic: { gradient: 'linear-gradient(180deg,#F8F2E2 0%,#F2EADB 100%)', accent: '#339274', accentSoft: 'rgba(51,146,116,0.12)', label: 'EPC',   icon: FolderKanban },
};

/* ---------- Doc tags ---------- */

export const DOC_TAGS: { value: DocTag; label: string; short: string; color: string; bg: string; icon: typeof FileText }[] = [
  { value: 'dpr',      label: 'DPR / Project Report', short: 'DPR',      color: '#3F6B82', bg: 'rgba(63,107,130,0.12)',  icon: ClipboardCheck },
  { value: 'contract', label: 'Contract / LOA',       short: 'Contract', color: '#6B5A8A', bg: 'rgba(107,90,138,0.12)', icon: FileText },
  { value: 'drawing',  label: 'Drawing / Layout',     short: 'Drawing',  color: '#1F5A47', bg: 'rgba(31,90,71,0.12)',   icon: ImageIcon },
  { value: 'noc',      label: 'NOC / Permit',         short: 'NOC',      color: '#A14A3A', bg: 'rgba(161,74,58,0.12)',  icon: ShieldCheck },
  { value: 'survey',   label: 'Survey / Site report', short: 'Survey',   color: '#9A7D3C', bg: 'rgba(154,125,60,0.14)', icon: MapPin },
  { value: 'other',    label: 'Other',                short: 'Other',    color: '#7C8A82', bg: 'rgba(124,138,130,0.14)', icon: Paperclip },
];

export const DOC_TAG_BY_VALUE: Record<DocTag, (typeof DOC_TAGS)[number]> =
  DOC_TAGS.reduce((acc, t) => ({ ...acc, [t.value]: t }), {} as Record<DocTag, (typeof DOC_TAGS)[number]>);

export function detectTagFromName(name: string): DocTag {
  const m = name.match(/^\[([^\]]+)\]\s*/);
  if (!m) return 'other';
  const t = m[1].toLowerCase();
  if (t.includes('dpr')) return 'dpr';
  if (t.includes('contract') || t.includes('loa')) return 'contract';
  if (t.includes('drawing') || t.includes('layout')) return 'drawing';
  if (t.includes('noc') || t.includes('permit')) return 'noc';
  if (t.includes('survey') || t.includes('site')) return 'survey';
  return 'other';
}

export function stripTagFromName(name: string): string {
  return name.replace(/^\[[^\]]+\]\s*/, '');
}

export function deriveDisplayName(originalName: string): string {
  const stripped = stripTagFromName(originalName);
  const dot = stripped.lastIndexOf('.');
  return dot > 0 ? stripped.slice(0, dot) : stripped;
}

export function fileExt(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot) : '';
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export function fileIcon(file: File): typeof FileText {
  if (file.type.startsWith('image/')) return ImageIcon;
  return FileText;
}
